import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { LOCAL_STORAGE_KEY } from '../constants';
import { appendLocalData, getLocalData } from '../helpers/storageHelper';
import { GetEstimateDto } from '../models/save-estimate-dto';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import {
  EstimateQueryParams,
  useFetchEstimateQuery,
} from '~/api/easy-estimator-api/estimate-api/estimateService';
import { shouldFetchFn } from '~/api/utils';
import { NotifierType } from '~/legacy/components';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectLoadEstimateCode,
  selectLoadEstimateKeys,
  updateCurrentEstimateCode,
  updateShowListOfEstimates,
} from '~/redux/slices/estimate';

type useFetchedEstimateReturn = TypedUseQueryHookResult<
  GetEstimateDto,
  EstimateQueryParams,
  AxiosBaseQueryFunction
> & {
  fetchedEstimate: GetEstimateDto | undefined;
  isFetchingEstimate: boolean;
};

const updateEstimateCode = (
  dispatch: ReturnType<typeof useAppDispatch>,
  fetchedEstimate: GetEstimateDto,
  loadEstimateCode: string | undefined
) => {
  if (!loadEstimateCode || !fetchedEstimate.revisions) {
    dispatch(updateCurrentEstimateCode(fetchedEstimate.estimateNumber));

    return;
  }

  const matchingRevision = fetchedEstimate.revisions.find(
    (revision: { code: string; estimateNumber: string }) =>
      revision.code === loadEstimateCode
  );

  if (matchingRevision) {
    dispatch(updateCurrentEstimateCode(matchingRevision.estimateNumber));
  } else if (loadEstimateCode === fetchedEstimate.code) {
    dispatch(updateCurrentEstimateCode(fetchedEstimate.estimateNumber));
  }
};

const useFetchedEstimate = (): useFetchedEstimateReturn => {
  const { t } = useTranslation();
  const loadEstimateCode =
    useAppSelector(selectLoadEstimateCode) ||
    getLocalData<string>(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);

  const loadEstimateKeys = useAppSelector(selectLoadEstimateKeys);

  const dispatch = useAppDispatch();

  const shouldFetchEstimate = useMemo(
    () =>
      shouldFetchFn({
        applySome: true,
        args: [
          loadEstimateCode,
          loadEstimateKeys?.code,
          loadEstimateKeys?.estimateNumber,
        ],
      }),
    [loadEstimateCode, loadEstimateKeys?.code, loadEstimateKeys?.estimateNumber]
  );

  const fetchEstimateQueryParams = useMemo(
    () => ({
      params: {
        code: loadEstimateKeys?.code ?? loadEstimateCode,
        customerEmail: loadEstimateKeys?.email,
        estimateNumber: loadEstimateKeys?.estimateNumber,
      },
    }),
    [
      loadEstimateCode,
      loadEstimateKeys?.code,
      loadEstimateKeys?.email,
      loadEstimateKeys?.estimateNumber,
    ]
  );

  const { fetchedEstimate, ...rest } = useFetchEstimateQuery(
    shouldFetchEstimate ? fetchEstimateQueryParams : skipToken,
    {
      refetchOnFocus: false,
      refetchOnReconnect: false,
      selectFromResult: (result) => ({
        fetchedEstimate: result.isError ? undefined : result.data,
        ...result,
      }),
    }
  );

  useEffect(() => {
    if (fetchedEstimate) {
      dispatch(updateShowListOfEstimates(Boolean(loadEstimateCode)));
      updateEstimateCode(dispatch, fetchedEstimate, loadEstimateCode);
    }
  }, [dispatch, fetchedEstimate, loadEstimateCode]);

  useEffect(() => {
    if (rest.isError) {
      const estimateNumber =
        loadEstimateKeys?.estimateNumber ?? loadEstimateCode;
      const url = new URL(document.location.href);

      appendLocalData(LOCAL_STORAGE_KEY.NOTIFICATIONS, {
        estimateNumber: estimateNumber,
        notifierType: NotifierType.ERROR,
        title: t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.TITLE'),
        description: t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.DESC'),
        message: t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.MSG'),
        duration: 0,
      });

      url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
      document.location.href = url.toString();
    }
  }, [rest.isError, dispatch, fetchedEstimate]);

  return {
    fetchedEstimate,
    isFetchingEstimate: rest.isFetching,
    ...rest,
  };
};

export default useFetchedEstimate;
